import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';

import I18n from 'utils/I18n';
import { StoreLocationUtils } from 'utils/StoreLocation';

import Carousel from './Carousel';
import { FLY_TO_SPEED, MAP_ZOOM_LEVELS } from './constants';
import Map from './Map';

import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import '@splidejs/react-splide/css';
import 'styles/tailwind.css';

export const flyToLocation = (map, location) => {
  if (map) {
    map.flyTo({
      center: [location.lon, location.lat],
      duration: FLY_TO_SPEED,
      zoom: MAP_ZOOM_LEVELS.DETAIL,
    });
  }
};

const StoreLocator = ({ stores }) => {
  const [selectedStore, setSelectedStore] = useState();
  const [sortedStores, setSortedStores] = useState([]);
  const mapRef = useRef();
  const carouselRef = useRef();

  const onCarouselSlideChange = useCallback(
    (_splideInstance, nextIndex) => {
      const newStore = sortedStores[nextIndex];
      setSelectedStore(newStore);
      flyToLocation(mapRef.current, newStore.geoLocation);
    },
    [sortedStores]
  );

  const onGeocoderResult = useCallback(
    ({ result }) => {
      const searchResultCoordinates = result.geometry.coordinates;
      const newSortedStores = StoreLocationUtils.sortContentfulLocationsByDistanceFromOrigin(
        stores,
        searchResultCoordinates
      );
      setSortedStores(newSortedStores);
      setSelectedStore(newSortedStores[0]);
      flyToLocation(mapRef.current, newSortedStores[0].geoLocation);
      if (carouselRef?.current?.splide) {
        carouselRef.current.splide.go(0);
      }
    },
    [stores]
  );

  /* istanbul ignore next */
  const scrollToStores = useCallback(() => {
    window.scrollTo({
      left: 0,
      top: document.getElementById('store-locations').offsetTop - 100,
      behavior: 'smooth',
    });
  }, []);

  const onMarkerClick = useCallback(
    store => {
      setSortedStores(
        StoreLocationUtils.sortContentfulLocationsByDistanceFromOrigin(stores, [
          store.geoLocation.lon,
          store.geoLocation.lat,
        ])
      );
      setSelectedStore(store);
      flyToLocation(mapRef.current, store.geoLocation);
      if (carouselRef?.current?.splide) {
        carouselRef.current.splide.go(0);
      }
    },
    [stores]
  );

  return (
    <div className="absolute inset-0">
      <Map
        mapRef={mapRef}
        onGeocoderResult={onGeocoderResult}
        onMarkerClick={onMarkerClick}
        selectedStore={selectedStore}
        stores={stores}
      />
      {sortedStores.length > 0 && (
        <Carousel
          carouselRef={carouselRef}
          onCarouselSlideChange={onCarouselSlideChange}
          selectedStore={selectedStore}
          sortedStores={sortedStores}
        />
      )}
      {!selectedStore && (
        <button
          type="button"
          onClick={scrollToStores}
          className="absolute bottom-3 left-2/4 z-10 -translate-x-2/4 rounded bg-black px-4 py-2 text-sm font-bold text-white"
        >
          {I18n.localize('storeLocator.viewAll')}
        </button>
      )}
    </div>
  );
};

StoreLocator.propTypes = {
  stores: PropTypes.array.isRequired,
};

export default StoreLocator;
